import React, { useEffect, useState } from 'react';
import { CgSpinner } from 'react-icons/cg';
import { FaTrashAlt } from 'react-icons/fa';
import AsyncSelect from 'react-select/async';
import { BookingQuestionsModal } from '../common/modals';
import PolarSwitch from '../common/formComponents/polarSwitch';
import PersonSelectorTransfer from '../common/formComponents/PersonSelectorTransfer';
import { SEC_ApiCall } from '../func';
import { toast } from 'react-toastify';
import './bookingstsBookingModal.css';

const MAX_EXTERNAL_ATTENDEES = 20;

/**
 * Modal for confirming a BOOKINGSTS selection with title/notes entry.
 * Mirrors the bookings module modal interaction pattern after slot selection.
 */
export default function BookingstsBookingModal({
    modalActive,
    setModalActive,
    roomName,
    date,
    startTime,
    endTime,
    proxyAllowed = false,
    attendeesAllowed = false,
    currentUserCogId = '',
    allowTimeEdit = false,
    timeOptions = [],
    onSubmit,
}) {
    const [title, setTitle] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [proxyEnabled, setProxyEnabled] = useState(false);
    const [proxyUser, setProxyUser] = useState(null);
    const [proxyOptions, setProxyOptions] = useState([]);
    const [proxyLoading, setProxyLoading] = useState(false);
    const [attendeesEnabled, setAttendeesEnabled] = useState(false);
    const [attendeesLoading, setAttendeesLoading] = useState(false);
    const [attendeesLoadError, setAttendeesLoadError] = useState('');
    const [allStaffPeople, setAllStaffPeople] = useState([]);
    const [selectedAttendees, setSelectedAttendees] = useState([]);
    const [externalAttendees, setExternalAttendees] = useState([{ id: Date.now(), name: '', email: '' }]);
    const [showNotesToAttendees, setShowNotesToAttendees] = useState(true);
    const [showOtherAttendeesInInvite, setShowOtherAttendeesInInvite] = useState(true);
    const [selectedStartTime, setSelectedStartTime] = useState('');
    const [selectedEndTime, setSelectedEndTime] = useState('');

    function timeStringToMinutes(value) {
        const parts = String(value || '').split(':');
        if (parts.length !== 2) {
            return NaN;
        }
        const hours = Number(parts[0]);
        const mins = Number(parts[1]);
        if (!Number.isInteger(hours) || !Number.isInteger(mins)) {
            return NaN;
        }
        return (hours * 60) + mins;
    }

    useEffect(() => {
        if (!modalActive) {
            setTitle('');
            setNotes('');
            setSubmitting(false);
            setProxyEnabled(false);
            setProxyUser(null);
            setProxyOptions([]);
            setProxyLoading(false);
            setAttendeesEnabled(false);
            setAttendeesLoading(false);
            setAttendeesLoadError('');
            setAllStaffPeople([]);
            setSelectedAttendees([]);
            setExternalAttendees([{ id: Date.now(), name: '', email: '' }]);
            setShowNotesToAttendees(true);
            setShowOtherAttendeesInInvite(true);
            setSelectedStartTime('');
            setSelectedEndTime('');
        }
    }, [modalActive]);

    useEffect(() => {
        if (!modalActive) {
            return;
        }
        setSelectedStartTime(startTime || '');
        setSelectedEndTime(endTime || '');
    }, [modalActive, startTime, endTime]);

    useEffect(() => {
        if (!allowTimeEdit || !modalActive || !Array.isArray(timeOptions) || timeOptions.length === 0) {
            return;
        }

        const firstOption = timeOptions[0];
        const fallbackEnd = timeOptions[1] || timeOptions[0];
        const startValue = timeOptions.includes(selectedStartTime) ? selectedStartTime : (startTime || firstOption);
        const endValue = timeOptions.includes(selectedEndTime) ? selectedEndTime : (endTime || fallbackEnd);

        const startMinutes = timeStringToMinutes(startValue);
        const endMinutes = timeStringToMinutes(endValue);
        if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
            const nextEnd = timeOptions.find((option) => timeStringToMinutes(option) > startMinutes) || fallbackEnd;
            setSelectedStartTime(startValue);
            setSelectedEndTime(nextEnd);
            return;
        }

        if (startValue !== selectedStartTime) {
            setSelectedStartTime(startValue);
        }
        if (endValue !== selectedEndTime) {
            setSelectedEndTime(endValue);
        }
    }, [allowTimeEdit, modalActive, timeOptions, selectedStartTime, selectedEndTime, startTime, endTime]);

    async function loadProxyOptions(input = '') {
        try {
            const params = input ? { q: input } : {};
            const response = await SEC_ApiCall('bookingsts/staff', params);
            return (response.items || []).map((item) => ({
                value: item.cogId,
                label: item.displayName,
            }));
        } catch (error) {
            console.error('failed to load proxy staff', error);
            return [];
        }
    }

    useEffect(() => {
        if (!proxyEnabled) {
            setProxyUser(null);
            setProxyOptions([]);
            setProxyLoading(false);
            return;
        }

        setProxyLoading(true);
        (async () => {
            try {
                const options = await loadProxyOptions('');
                if (!Array.isArray(options) || options.length === 0) {
                    toast.error('❌ Failed to load proxy users. Please try again.', { autoClose: 4000, position: 'top-right' });
                    setProxyEnabled(false);
                    return;
                }
                setProxyOptions(options);
            } catch (error) {
                console.error('proxy load failed', error);
                toast.error('❌ Failed to load proxy users. Please try again.', { autoClose: 4000, position: 'top-right' });
                setProxyEnabled(false);
            } finally {
                setProxyLoading(false);
            }
        })();
    }, [proxyEnabled]);

    useEffect(() => {
        if (!attendeesAllowed) {
            setAttendeesEnabled(false);
            setAttendeesLoading(false);
            setAttendeesLoadError('');
            setAllStaffPeople([]);
            setSelectedAttendees([]);
            setExternalAttendees([{ id: Date.now(), name: '', email: '' }]);
            setShowNotesToAttendees(true);
            setShowOtherAttendeesInInvite(true);
        }
    }, [attendeesAllowed]);

    useEffect(() => {
        if (!attendeesEnabled) {
            setAttendeesLoading(false);
            setAttendeesLoadError('');
            setAllStaffPeople([]);
            setSelectedAttendees([]);
            setExternalAttendees([{ id: Date.now(), name: '', email: '' }]);
            setShowNotesToAttendees(true);
            setShowOtherAttendeesInInvite(true);
            return;
        }

        if (!attendeesAllowed) {
            return;
        }

        setAttendeesLoading(true);
        setAttendeesLoadError('');
        (async () => {
            try {
                const response = await SEC_ApiCall('bookingsts/staff', {});
                const people = Array.isArray(response?.items) ? response.items : [];
                setAllStaffPeople(people);
            } catch (error) {
                console.error('failed to load attendees list', error);
                toast.error('❌ Failed to load attendees list. Please try again.', { autoClose: 4000, position: 'top-right' });
                setAttendeesLoadError('Failed to load staff list.');
                setAllStaffPeople([]);
            } finally {
                setAttendeesLoading(false);
            }
        })();
    }, [attendeesAllowed, attendeesEnabled]);

    function handleExternalAttendeeChange(id, field, value) {
        setExternalAttendees((previousRows) => previousRows.map((row) => {
            if (row.id !== id) {
                return row;
            }
            return {
                ...row,
                [field]: value,
            };
        }));
    }

    function handleAddExternalAttendee() {
        if (externalAttendees.length >= MAX_EXTERNAL_ATTENDEES) {
            toast.error(`❌ Only ${MAX_EXTERNAL_ATTENDEES} external attendees are allowed.`, { autoClose: 4000, position: 'top-right' });
            return;
        }
        setExternalAttendees((previousRows) => [
            ...previousRows,
            { id: Date.now() + Math.floor(Math.random() * 1000), name: '', email: '' },
        ]);
    }

    function handleRemoveExternalAttendee(id) {
        setExternalAttendees((previousRows) => {
            const filteredRows = previousRows.filter((row) => row.id !== id);
            if (filteredRows.length === 0) {
                return [{ id: Date.now(), name: '', email: '' }];
            }
            return filteredRows;
        });
    }

    function getCleanExternalAttendees() {
        const cleanedRows = externalAttendees
            .map((row) => ({
                name: String(row?.name || '').trim(),
                email: String(row?.email || '').trim(),
            }))
            .filter((row) => row.name || row.email);

        const validRows = [];
        const seen = new Set();
        for (const row of cleanedRows) {
            if (!row.name || !row.email) {
                throw new Error('Each external attendee requires both a name and an email address.');
            }
            const dedupeKey = `${row.name.toLowerCase()}|${row.email.toLowerCase()}`;
            if (seen.has(dedupeKey)) {
                continue;
            }
            seen.add(dedupeKey);
            validRows.push(row);
        }
        return validRows;
    }

    function handleStartTimeChange(nextStartTime) {
        setSelectedStartTime(nextStartTime);
        const startMinutes = timeStringToMinutes(nextStartTime);
        const endMinutes = timeStringToMinutes(selectedEndTime);
        if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
            const nextValidEnd = (timeOptions || []).find((option) => timeStringToMinutes(option) > startMinutes);
            if (nextValidEnd) {
                setSelectedEndTime(nextValidEnd);
            }
        }
    }

    async function handleSubmit() {
        if (!String(title || '').trim()) {
            toast.error('\\u274c Title is required.', { autoClose: 4000, position: 'top-right' });
            return;
        }

        if (proxyEnabled && !proxyUser) {
            toast.error('❌ Please select a user for proxy booking.', { autoClose: 4000, position: 'top-right' });
            return;
        }

        const proxyFor =
            proxyEnabled && proxyUser && proxyUser.value !== currentUserCogId
                ? proxyUser.value
                : null;

        const effectiveStartTime = allowTimeEdit ? selectedStartTime : startTime;
        const effectiveEndTime = allowTimeEdit ? selectedEndTime : endTime;
        const startMinutes = timeStringToMinutes(effectiveStartTime);
        const endMinutes = timeStringToMinutes(effectiveEndTime);
        if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
            toast.error('❌ Finish time must be after start time.', { autoClose: 4000, position: 'top-right' });
            return;
        }

        setSubmitting(true);
        try {
            const external = attendeesEnabled ? getCleanExternalAttendees() : [];
            const internal = attendeesEnabled
                ? selectedAttendees
                    .map((attendee) => String(attendee?.cogId || '').trim())
                    .filter((attendeeId) => Boolean(attendeeId))
                : [];

            await onSubmit({
                title: String(title || '').trim(),
                notes: String(notes || '').trim(),
                proxyFor,
                attendees: {
                    internal,
                    external,
                },
                omitNotesInInvite: !showNotesToAttendees,
                omitOtherAttendeesInInvite: !showOtherAttendeesInInvite,
                startTime: effectiveStartTime,
                endTime: effectiveEndTime,
            });
            setModalActive(false);
        } catch (error) {
            if (String(error?.message || '').includes('external attendee')) {
                toast.error(`❌ ${error.message}`, { autoClose: 4000, position: 'top-right' });
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <BookingQuestionsModal modalActiveState={modalActive} setStateFunction={setModalActive}>
            <div className='bookingstsModalContainer'>
                <div className='bookingstsModalDetails'>
                    <p>{roomName}</p>
                    <p>{date} {startTime} - {endTime}</p>
                </div>

                {allowTimeEdit && (
                    <div className='bookingstsModalTimeEditRow'>
                        <div className='bookingstsModalField bookingstsModalTimeField'>
                            <label htmlFor='bookingstsStartTime'>Start time</label>
                            <select
                                id='bookingstsStartTime'
                                value={selectedStartTime}
                                onChange={(event) => handleStartTimeChange(event.target.value)}
                            >
                                {timeOptions.map((timeValue) => (
                                    <option key={`bookingsts-start-${timeValue}`} value={timeValue}>{timeValue}</option>
                                ))}
                            </select>
                        </div>
                        <div className='bookingstsModalField bookingstsModalTimeField'>
                            <label htmlFor='bookingstsEndTime'>Finish time</label>
                            <select
                                id='bookingstsEndTime'
                                value={selectedEndTime}
                                onChange={(event) => setSelectedEndTime(event.target.value)}
                            >
                                {timeOptions.map((timeValue) => (
                                    <option key={`bookingsts-end-${timeValue}`} value={timeValue}>{timeValue}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <div className='bookingstsModalField'>
                    <label htmlFor='bookingstsTitle'>Title</label>
                    <input
                        id='bookingstsTitle'
                        type='text'
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder='Meeting purpose'
                    />
                </div>

                <div className='bookingstsModalField'>
                    <label htmlFor='bookingstsNotes'>Notes (optional)</label>
                    <textarea
                        id='bookingstsNotes'
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        placeholder='Additional details'
                    />
                </div>

                {proxyAllowed && (
                    <>
                        <div className='bookingstsProxyRow'>
                            <span className='bookingstsProxyLabel'>Proxy Booking</span>
                            <PolarSwitch data={proxyEnabled} setData={setProxyEnabled} loading={proxyLoading} />
                        </div>
                        {proxyEnabled && (
                            <AsyncSelect
                                cacheOptions
                                defaultOptions={proxyOptions}
                                loadOptions={loadProxyOptions}
                                value={proxyUser}
                                onChange={setProxyUser}
                                className='bookingstsProxySelect'
                                placeholder='Select staff member'
                            />
                        )}
                    </>
                )}

                {attendeesAllowed && (
                    <>
                        <div className='bookingstsAttendeesRow'>
                            <span className='bookingstsAttendeesLabel'>Attendees</span>
                            <PolarSwitch
                                data={attendeesEnabled}
                                setData={setAttendeesEnabled}
                                loading={attendeesLoading}
                            />
                        </div>
                        {attendeesEnabled && (
                            <>
                                <div className='bookingstsInvitePrivacyOptions'>
                                    <div className='bookingstsAttendeesRow'>
                                        <span className='bookingstsAttendeesLabel'>Show notes to attendees</span>
                                        <PolarSwitch
                                            data={showNotesToAttendees}
                                            setData={setShowNotesToAttendees}
                                            loading={false}
                                        />
                                    </div>
                                    <div className='bookingstsAttendeesRow'>
                                        <span className='bookingstsAttendeesLabel'>Show other attendees in email invite</span>
                                        <PolarSwitch
                                            data={showOtherAttendeesInInvite}
                                            setData={setShowOtherAttendeesInInvite}
                                            loading={false}
                                        />
                                    </div>
                                </div>
                                {attendeesLoading ? (
                                    <div className='bookingstsStaffSelectorStatus'>Loading staff</div>
                                ) : attendeesLoadError ? (
                                    <div className='bookingstsStaffSelectorStatus bookingstsStaffSelectorStatusError'>
                                        Failed to load staff list.
                                    </div>
                                ) : (
                                    <PersonSelectorTransfer
                                        allPeople={allStaffPeople}
                                        nameKey='displayName'
                                        idKey='cogId'
                                        selectedPeople={selectedAttendees}
                                        onChange={setSelectedAttendees}
                                        allTitle='All staff'
                                        selectedTitle='Selected internal attendees'
                                        enableLeftSearch
                                        leftSearchPlaceholder='Search staff'
                                        disabled={false}
                                    />
                                )}
                                <div className='bookingstsExternalAttendeesWrap'>
                                    <label className='bookingstsExternalAttendeesLabel'>External Attendees</label>
                                    {externalAttendees.map((externalAttendee) => (
                                        <div className='bookingstsExternalAttendeeRow' key={externalAttendee.id}>
                                            <input
                                                type='text'
                                                value={externalAttendee.name}
                                                onChange={(event) => handleExternalAttendeeChange(externalAttendee.id, 'name', event.target.value)}
                                                placeholder='Person name'
                                            />
                                            <input
                                                type='email'
                                                value={externalAttendee.email}
                                                onChange={(event) => handleExternalAttendeeChange(externalAttendee.id, 'email', event.target.value)}
                                                placeholder='Person email'
                                            />
                                            <button
                                                type='button'
                                                className='bookingstsExternalAttendeeRemove'
                                                onClick={() => handleRemoveExternalAttendee(externalAttendee.id)}
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        </div>
                                    ))}

                                    <button
                                        type='button'
                                        className='bookingstsExternalAttendeeAdd'
                                        onClick={handleAddExternalAttendee}
                                    >
                                        + Add external attendee ({externalAttendees.length}/{MAX_EXTERNAL_ATTENDEES})
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}

                <div className='bookingstsModalSubmitArea'>
                    <button
                        className='formSubmitButton'
                        type='button'
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? <CgSpinner className='submit-button-spinner' /> : 'Submit'}
                    </button>
                </div>
            </div>
        </BookingQuestionsModal>
    );
}

